import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientTrading() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSimulations();
  }, [token]);

  const fetchSimulations = async () => {
    try {
      const response = await axios.get(`${API}/trading/simulations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSimulations(response.data);
    } catch (error) {
      console.error('Failed to fetch simulations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      neutral: 'status-neutral',
      active: 'status-active',
      suspended: 'status-suspended'
    };
    const labels = {
      neutral: 'Neutre',
      active: 'Actif',
      suspended: 'Suspendu'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {simulations.length === 0 ? (
          <Card className="card-premium">
            <CardContent className="py-12 text-center">
              <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun trading actif</p>
              <p className="text-sm text-muted-foreground mt-1">
                Contactez votre analyste pour configurer votre trading
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {simulations.map(sim => (
              <Card key={sim.id} className="card-premium" data-testid={`simulation-${sim.id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{sim.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {sim.num_positions} positions • Profil {sim.risk_profile}
                      </p>
                    </div>
                    {getStatusBadge(sim.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 rounded-sm bg-white/5">
                      <p className="text-xs text-muted-foreground">{t('total_profit')}</p>
                      <p className={`text-xl font-bold ${sim.total_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {sim.total_profit >= 0 ? '+' : ''}€{sim.total_profit?.toFixed(2)}
                      </p>
                    </div>
                    <div className="p-4 rounded-sm bg-white/5">
                      <p className="text-xs text-muted-foreground">Total trades</p>
                      <p className="text-xl font-bold">{sim.total_trades}</p>
                    </div>
                    <div className="p-4 rounded-sm bg-white/5">
                      <p className="text-xs text-muted-foreground">{t('winning_trades')}</p>
                      <p className="text-xl font-bold text-emerald-500">{sim.winning_trades}</p>
                    </div>
                    <div className="p-4 rounded-sm bg-white/5">
                      <p className="text-xs text-muted-foreground">{t('losing_trades')}</p>
                      <p className="text-xl font-bold text-red-500">{sim.losing_trades}</p>
                    </div>
                  </div>

                  {/* Performance Chart */}
                  {sim.performance_data && sim.performance_data.length > 0 && (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={sim.performance_data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis dataKey="day" stroke="rgba(255,255,255,0.5)" />
                          <YAxis stroke="rgba(255,255,255,0.5)" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))' 
                            }}
                            formatter={(value) => [`${value.toFixed(2)}%`, 'Performance']}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#D4AF37" 
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Positions */}
                  {sim.positions && sim.positions.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium mb-3">{t('positions')}</h4>
                      <div className="overflow-x-auto">
                        <table className="table-premium">
                          <thead>
                            <tr>
                              <th>Actif</th>
                              <th>Montant</th>
                              <th>Entrée</th>
                              <th>Sortie</th>
                              <th>P/L</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sim.positions.slice(0, 5).map((pos, idx) => (
                              <tr key={pos.id || idx}>
                                <td className="font-mono">{pos.asset}</td>
                                <td>€{pos.amount}</td>
                                <td className="font-mono">{pos.entry_price}</td>
                                <td className="font-mono">{pos.exit_price}</td>
                                <td className={`font-mono ${pos.is_win ? 'text-emerald-500' : 'text-red-500'}`}>
                                  {pos.profit_loss >= 0 ? '+' : ''}€{pos.profit_loss?.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
